import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { Toaster } from 'sonner';
import '@/App.css';

// Pages
import BRAND from '@/brand';
import FlyttetSpaerre from '@/components/FlyttetSpaerre';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import LogIndPage from '@/pages/LogIndPage';
import RegisterPage from '@/pages/RegisterPage';
import BundlePage from '@/pages/BundlePage';
import DashboardPage from '@/pages/DashboardPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import InvitePage from '@/pages/InvitePage';
import DeleteCompanyPage from '@/pages/DeleteCompanyPage';
import SignPage from '@/pages/SignPage';

// Private Route Component (redirect to login if not logged in)
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-th-body flex items-center justify-center">
        <div className="text-th-text-secondary">Indlæser...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-th-body flex items-center justify-center">
        <div className="text-th-text-secondary">Indlæser...</div>
      </div>
    );
  }

  if (user) {
    // Foerste login for en ny bruger fra Google/Microsoft (ny=1, sat af
    // OAuth-callbacken) lander paa Selskaber — der er intet i indbakken endnu.
    // Uden det her ville denne omdirigering komme login-siden i forkoebet.
    const ny = new URLSearchParams(location.search).get('ny') === '1';
    return <Navigate to={ny ? '/selskaber' : '/dashboard'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* DigitalBox' forside ER login. HentPost beholder sin marketingforside —
          brandene deler ikke forside, og det er netop det, brand-laget er til.
          PublicRoute sender en allerede indlogget bruger videre til appen. */}
      <Route path="/" element={
        <PublicRoute>{BRAND.key === 'digitalbox' ? <LogIndPage /> : <LandingPage />}</PublicRoute>
      } />
      <Route path="/log-ind" element={<PublicRoute><LogIndPage /></PublicRoute>} />
      <Route path="/opret-konto" element={<PublicRoute><LogIndPage tilstand="opret" /></PublicRoute>} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route path="/bundle" element={<BundlePage />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite" element={<InvitePage />} />
      {/* Linket kommer fra en mail — ingen login-krav, tokenet er beviset */}
      <Route path="/slet-selskab" element={<DeleteCompanyPage />} />
      {/* Offentlig underskriftsside — IKKE wrappet i PublicRoute, da baade
          indloggede og ikke-indloggede skal kunne aabne et signeringslink. */}
      <Route path="/sign" element={<SignPage />} />
      <Route
        path="/dashboard"
        element={<Navigate to="/indbakke" replace />}
      />
      <Route path="/indbakke" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/indbakke/:companyId" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/videresend" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/profil" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      {/* Ikke "/api" — den sti proxies til backenden i nginx */}
      <Route path="/api-adgang" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/dokumenter/indstillinger" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/dokumenter/api" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/opret" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/selskaber" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/adressebog" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/dokumenter" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/modtagere" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/admin" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/admin/selskaber" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/admin/indbakke" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/admin/onboarding" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <FlyttetSpaerre />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: 'var(--toast-bg)',
                  border: '1px solid var(--toast-border)',
                  color: 'var(--toast-color)'
                }
              }}
            />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
